import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Camera, CirclePlus, Pencil, Trash } from 'lucide-react';
import { FileUpload, RadioButton } from 'fluent-components';
import { SelectField, DateField, ErrorToast, InputField } from './index';
import {
  doctorSchema,
  DEFAULT_ADD_DOCTOR_SCHEMA_VALUES,
  DoctorFormInputs,
} from './types';
import { ROUTE } from '@/routes';
import {
  languages,
  prefixes,
  qualifications,
  // gender,
} from '@/constants/doctor-management';
import { patchData, postData } from '@/lib/axios';
import { extractErrorDetails } from '@/lib/utils';
import {
  IMAGE_UPLOAD,
  URL_PREFIX,
  USERS,
} from '@/constants/api-endpoints.constants';
import { useQuery } from '@apollo/client';
import {
  DOCTOR_FEATURE_DROPDOWN_LIST,
  GET_CITIES_BY_COUNTRY,
  GET_DOCTOR_BY_ID,
  GET_STATES_BY_COUNTRY,
} from '@/graphql/queries/doctor-management';
import { mapFormDataToApiFormat, userProfileDataMapper } from './helper';
import { AxiosError } from 'axios';
import { IMAGE_UPLOAD_SIZE } from '@/constants';
import { z } from 'zod';
import { MultiSelectLanguage } from './MultiSelectLanguage';
import ConfirmationPopup from '@/components/common/ConfirmationPopup';
import { addDays, subMonths } from 'date-fns';
import { PriceInputField } from './PriceInputField';
import { mixPanelServiceInstance } from '@/analytics/mixpanel';
import { mixPanelEvents } from '@/analytics/mixpanel/events';
import useUserInfo from '@/components/Authenticate/useUserInfo';
import { mapConcepts } from './utils';
interface TelecomEntry {
  system: string;
  value: string;
}

interface OutletContextType {
  isEditing: boolean;
  onEditToggle: (editing: boolean) => void;
}

const FormSection = ({ children }: { children: React.ReactNode }) => (
  <div className="mt-10">{children}</div>
);

export type ProfileImageDetailsProps = {
  name: string;
  url: string;
  type: string;
} | null;

export type UserFormInputs = z.infer<typeof doctorSchema>;

const DoctorForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { data: doctorData } = useQuery(GET_DOCTOR_BY_ID, {
    variables: { id },
    skip: !isEditMode,
  });
  const { onEditToggle } = useOutletContext<OutletContextType>() || {
    onEditToggle: () => {},
    isEditing: false,
  };
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [msaContractFileName, setMsaContractFileName] = useState<string | null>(
    null
  );
  const [indemnityFileName, setIndemnityFileName] = useState<string | null>(
    null
  );
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [image, setImage] = useState<string | ArrayBuffer | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [educationRows, setEducationRows] = useState([
    { years: '', body: '', title: '' },
  ]);
  const [selectedDisplayTop, setSelectedDisplayTop] = useState<number | null>(
    null
  );
  const [selectedDisplayBottom, setSelectedDisplayBottom] = useState<
    number | null
  >(null);
  const { userInfo } = useUserInfo();

  const { data: featureData } = useQuery(DOCTOR_FEATURE_DROPDOWN_LIST);
  const { data: citiesData } = useQuery(GET_CITIES_BY_COUNTRY, {
    variables: {
      url: `https://fluentinhealth.com/FHIR/ValueSet/Cities-${
        selectedCountry || 'IN'
      }`,
    },
  });

  const { data: stateData } = useQuery(GET_STATES_BY_COUNTRY, {
    variables: {
      url: `https://fluentinhealth.com/FHIR/ValueSet/states-${
        selectedCountry || 'IN'
      }`,
    },
  });

  console.log('cities data: ', citiesData);
  console.log('states data: ', stateData);

  const fileUploadRef = useRef<HTMLInputElement>(null);
  const form = useForm<DoctorFormInputs>({
    resolver: zodResolver(doctorSchema),
    defaultValues: DEFAULT_ADD_DOCTOR_SCHEMA_VALUES,
    mode: 'onChange',
  });

  const {
    formState: { errors, isValid },
    handleSubmit,
    reset,
  } = form;

  useEffect(() => {
    console.log('Form Errors:', errors);
    console.log('Is Form Valid:', isValid);
  }, [errors, isValid]);

  useEffect(() => {
    if (isEditMode && doctorData?.doctor) {
      const profile = doctorData.doctor;
      const formattedData = {
        firstName: profile.name?.[0]?.given?.[0] || '',
        lastName: profile.name?.[0]?.family || '',
        phoneNumber: profile.telecom?.[1]?.value || '',
        email:
          profile.telecom?.find((t: TelecomEntry) => t.system === 'email')
            ?.value || '',
        city: profile.address?.[0]?.city || '',
        country: profile.address?.[0]?.country || null,
        state: profile.address?.[0].state || '',
        address1: profile.address?.[0]?.address1 || '',
        address2: profile.address?.[0]?.address2 || '',
        zipCode: profile.address?.[0]?.zipCode || '',
        gender: profile.gender || 'male',
        // birthDate: profile.birthDate ? new Date(profile.birthDate) : undefined,
        languages: profile.languages || [],
        aboutMe: profile.aboutMe || '',
        prefix: profile.name?.[0]?.prefix || '',
        speciality:
          profile.practitionerRole?.[0]?.specialty?.[0]?.coding?.[0]?.display ||
          '',
        stamp: undefined,
        signature: undefined,
        qualification: profile.qualification || '',
        registrationNumber: profile.registrationNo || '',
        msaSigningDate: profile.msaSigningDate
          ? new Date(profile.msaSigningDate)
          : undefined,
        practitionerStartDate: profile.practitionerStartDate
          ? new Date(profile.practitionerStartDate)
          : undefined,
        practitionerEndDate: profile.practitionerEndDate
          ? new Date(profile.practitionerEndDate)
          : undefined,
        indemnityValidity: profile.indemnityValidity
          ? new Date(profile.indemnityValidity)
          : undefined,
        registrationValidity: profile.registrationValidity
          ? new Date(profile.registrationValidity)
          : undefined,
        pricing: profile.pricing || '',
        educationRows: profile.education || [
          { years: '', body: '', title: '' },
        ],
      };

      reset(formattedData); // Reset the form with formatted data
      if (profile.image) {
        setImage(profile.image);
        setImageUrl(profile.image);
        setSelectedFileName('Current Profile Image');
      }
    }
  }, [doctorData, isEditMode, reset]);

  useEffect(() => {
    if (id) {
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  }, [id]);

  const addRow = () =>
    setEducationRows([...educationRows, { years: '', body: '', title: '' }]);

  const deleteRow = (index: number) =>
    setEducationRows(educationRows.filter((_, i) => i !== index));

  const handleDisplayTopChange = (index: number) => {
    // Uncheck the radio button in other rows
    setSelectedDisplayTop(index);

    if (selectedDisplayBottom === index) {
      setSelectedDisplayBottom(null);
    }
  };

  // Handle radio button changes for displayBottom
  const handleDisplayBottomChange = (index: number) => {
    // Uncheck the radio button in other rows
    setSelectedDisplayBottom(index);

    if (selectedDisplayTop === index) {
      setSelectedDisplayTop(null);
    }
  };

  const handleImageChange = (file: File) => {
    if (file) {
      if (file.size > IMAGE_UPLOAD_SIZE) {
        setImageError('File size exceeds 2MB');
        return;
      }

      setImageError(null);
      const reader = new FileReader();
      reader.onloadend = () => setImage(reader.result);
      reader.readAsDataURL(file);

      uploadImageToBackend(file);
      setSelectedFileName(file.name);
    }
  };

  const uploadImageToBackend = async (file: File) => {
    try {
      const { url } = await postData(IMAGE_UPLOAD, file, {
        'content-type': file.type,
      });
      setImageUrl(url);
    } catch (error) {
      console.error('Error uploading image:', error);
      setImageError('Error uploading image. Please try again.');
      throw error;
    }
  };

  useEffect(() => {
    // @ts-expect-error@ts-ignore
    ErrorToast({ errors });
  }, [errors]);

  const handleFormSubmit = async (data: DoctorFormInputs) => {
    try {
      let apiData;
      if (isEditMode && doctorData?.doctor?.id) {
        const formattedData = mapFormDataToApiFormat(
          doctorData.doctor.id,
          data,
          image as string | null
        );
        console.log('Formatted data for API:', formattedData);
        apiData = await patchData(
          `Practitioner/${doctorData.doctor.id}`,
          formattedData
        );

        mixPanelServiceInstance.track(
          mixPanelEvents.DOCTOR_EVENTS.EDIT_DOCTOR_COMPLETED,
          {
            doctor_manager_id: userInfo?.sub,
            doctor_id: doctorData?.doctor?.id,
            user_id: userInfo?.sub,
            dr_profile_image_url: imageUrl,
            dr_first_name: data.firstName,
            dr_last_name: data.lastName,
            dr_phone_number: data.phoneNumber,
            dr_email: data.email,
            dr_msa_sign_date: data.msaSigningDate,
            dr_start_date: data.practitionerStartDate,
          }
        );
      } else {
        const doctorFormattedData = userProfileDataMapper(data);
        const { medplumId } = await postData(USERS, doctorFormattedData);
        apiData = await postData(
          URL_PREFIX,
          mapFormDataToApiFormat(medplumId, data, imageUrl)
        );
      }
      console.log('API response:', apiData);

      mixPanelServiceInstance.track(
        mixPanelEvents.DOCTOR_EVENTS.ADD_DOCTOR_COMPLETED,
        {
          doctor_manager_id: userInfo?.sub,
          dr_profile_image_url: imageUrl,
          dr_first_name: data.firstName,
          dr_last_name: data.lastName,
          dr_phone_number: data.phoneNumber,
          dr_email: data.email,
          dr_msa_sign_date: data.msaSigningDate,
          dr_start_date: data.practitionerStartDate,
        }
      );

      navigate(ROUTE.DOCTOR_MANAGEMENT);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const extractedDetails = extractErrorDetails(
          error.response?.data?.message
        );
        const errors = {
          errorMessageField: { message: extractedDetails.errorMessage },
          traceIdField: { message: `Trace ID: ${extractedDetails.traceId}` },
        };
        ErrorToast({ errors });
      } else {
        ErrorToast({
          errors: { unknownField: { message: 'An unknown error occurred' } },
        });
      }
    }
  };

  const handleImageClick = () => {
    if (fileUploadRef.current) {
      fileUploadRef.current.click();
    }
  };

  const handleCancelClick = () => {
    mixPanelServiceInstance.track(
      mixPanelEvents.DOCTOR_EVENTS.EDIT_DOCTOR_CANCELLED,
      {
        doctor_manager_id: userInfo?.sub,
      }
    );
    setIsEditing(false);
    navigate(ROUTE.DOCTOR_MANAGEMENT);
  };

  const toggleEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const newEditingState = !isEditing;
    setIsEditing(newEditingState);
    mixPanelServiceInstance.track(
      mixPanelEvents.DOCTOR_EVENTS.EDIT_DOCTOR_STARTED,
      {
        doctor_manager_id: userInfo?.sub,
        doctor_id: id,
        user_id: id,
      }
    );

    // Call the edit toggle method passed from parent
    if (onEditToggle) {
      onEditToggle(newEditingState);
    }
  };

  const { specialization, countries, speciality, educationTitle } =
    featureData || {};

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="border-[1px] border-borderGray bg-white p-8 rounded-lg shadow-sm"
        >
          <div className="w-4/5 mr-auto">
            <div className="flex justify-between items-center">
              <h2 className="text-lg text-primary font-medium text-left">
                PERSONAL DETAILS
              </h2>
              {doctorData && !isEditing && (
                <Button
                  variant="link"
                  type="button"
                  className="text-sm text-iris-160 font-medium flex gap-2 items-center mr-[-24%]"
                  onClick={toggleEdit}
                >
                  <Pencil size={14} />
                  Edit Doctor
                </Button>
              )}
            </div>

            <FormSection>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <SelectField
                  isMandatory
                  control={form.control}
                  disabled={!isEditing}
                  name="prefix"
                  label="Prefix"
                  placeholder="Select prefix"
                  options={prefixes}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <FormItem>
                  <FormLabel>Profile image</FormLabel>
                  <FileUpload
                    ref={fileUploadRef}
                    onChange={(file: File) => {
                      if (file) {
                        handleImageChange(file);
                        setSelectedFileName(file.name);
                      }
                    }}
                    defaultFileName={selectedFileName}
                    accept="image/png, image/jpg, image/jpeg"
                    maxFileSize={IMAGE_UPLOAD_SIZE}
                  />
                  <p className="text-sm mt-2 font-normal text-gray-500">
                    PNG, JPG up to 5MB
                  </p>
                  {imageError && (
                    <p className="text-red-500 text-xs mt-1">{imageError}</p>
                  )}
                </FormItem>

                <div
                  className="relative w-24 h-24 flex items-center justify-center rounded-full bg-gray-200 mb-3 group"
                  onClick={handleImageClick}
                >
                  {image ? (
                    <>
                      <img
                        src={image as string}
                        alt="Profile"
                        className="w-full h-full object-cover rounded-full"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Camera size="1.2rem" className="text-white" />
                      </div>
                    </>
                  ) : (
                    <Camera size="1.2rem" className="text-gray-500" />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6 mb-8">
                <InputField
                  isMandatory
                  control={form.control}
                  name="firstName"
                  disabled={!isEditing}
                  placeholder="Enter First name"
                  label="First name"
                  onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                    e.target.value = e.target.value.replace(/[^A-Za-z]/g, '');
                  }}
                />

                <InputField
                  isMandatory
                  control={form.control}
                  name="lastName"
                  disabled={!isEditing}
                  placeholder="Enter Last name"
                  label="Last name"
                  onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                    e.target.value = e.target.value.replace(/[^A-Za-z]/g, '');
                  }}
                />
              </div>

              <FormField
                control={form.control}
                disabled={!isEditing}
                name="aboutMe"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>About Me</FormLabel>
                    <FormControl className="focus-within:outline-none focus-within:ring-2">
                      <textarea
                        {...field}
                        placeholder="Write about yourself..."
                        className="w-full p-2 border rounded-md border-gray-300 shadow-sm sm:text-sm"
                        rows={4}
                        maxLength={500}
                      />
                    </FormControl>
                    <p className="text-sm text-right font-normal text-gray-400 mt-1">
                      {field.value && <>{field.value.length}/500</>}
                    </p>
                    <FormMessage className="opacity-70" />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <FormField
                  control={form.control}
                  name="languages"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>Languages</FormLabel>
                      <MultiSelectLanguage
                        options={languages}
                        onValueChange={field.onChange}
                        placeholder="Select"
                        defaultValue={field.value}
                        animation={2}
                        maxCount={3}
                        className="w-full"
                        disabled={!isEditing}
                      />
                      <FormMessage className="opacity-70">
                        {fieldState.error?.message}
                      </FormMessage>
                    </FormItem>
                  )}
                />

                <InputField
                  isMandatory
                  control={form.control}
                  name="email"
                  disabled={!isEditing}
                  placeholder="Enter email address"
                  label="Personal email address"
                  type="email"
                  maxLength={50}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <InputField
                  isMandatory
                  control={form.control}
                  name="address1"
                  disabled={!isEditing}
                  placeholder="Address Line 1"
                  label="Current address"
                  type="text"
                  maxLength={100}
                />

                <InputField
                  control={form.control}
                  name="address2"
                  disabled={!isEditing}
                  placeholder="Address Line 2"
                  label="Current address"
                  type="text"
                  maxLength={100}
                  hideLabel
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <SelectField
                  control={form.control}
                  disabled={!isEditing}
                  name="country"
                  placeholder="Country *"
                  options={mapConcepts(countries?.[0])}
                  onChangeValue={(value) => setSelectedCountry(value)}
                />

                <InputField
                  control={form.control}
                  name="zipCode"
                  disabled={!isEditing}
                  placeholder="Pincode *"
                  type="text"
                  maxLength={10}
                  minLength={4}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <SelectField
                  control={form.control}
                  disabled={!isEditing}
                  name="state"
                  placeholder="State/Province"
                  options={[]}
                  onChangeValue={(value) => setSelectedCountry(value)} // Set the selected country
                />
                <SelectField
                  control={form.control}
                  disabled={!isEditing}
                  name="city"
                  placeholder="City *"
                  options={mapConcepts(citiesData?.cities?.[0])}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <InputField
                  isMandatory
                  control={form.control}
                  name="phoneNumber"
                  disabled={!isEditing}
                  placeholder="Phone no."
                  label="Phone number"
                  type="text"
                  maxLength={10}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <FormItem>
                  <FormLabel>Signature</FormLabel>
                  <FileUpload
                    onChange={(file: File) =>
                      console.log('Selected File:', file)
                    }
                  />
                  <p className="text-sm mt-2 font-normal text-gray-500">
                    PNG, JPG up to 5MB
                  </p>
                </FormItem>
                <FormItem>
                  <FormLabel>Stamp</FormLabel>
                  <FileUpload
                    onChange={(file: File) =>
                      console.log('Selected PDF:', file)
                    }
                  />
                  <p className="text-sm mt-2 font-normal text-gray-500">
                    PNG, JPG up to 5MB
                  </p>
                </FormItem>
              </div>

              <h2 className="text-lg text-primary font-medium text-left mb-6 mt-10">
                PROFESSIONAL AND EDUCATIONAL DETAILS
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <SelectField
                  isMandatory
                  control={form.control}
                  disabled={!isEditing}
                  name="specialization"
                  label="Specialization"
                  placeholder="Select specialization"
                  options={mapConcepts(specialization?.[0])}
                />
                <SelectField
                  isMandatory
                  control={form.control}
                  disabled={!isEditing}
                  name="speciality"
                  label="Speciality"
                  placeholder="Select speciality"
                  options={mapConcepts(speciality?.[0])}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <SelectField
                  isMandatory
                  control={form.control}
                  disabled={!isEditing}
                  name="qualification"
                  label="Qualification"
                  placeholder="Select qualification"
                  options={qualifications}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <InputField
                  isMandatory
                  control={form.control}
                  name="registrationNumber"
                  disabled={!isEditing}
                  placeholder="Registration no."
                  label="Registration number"
                  type="text"
                  minLength={5}
                  maxLength={30}
                />

                <DateField
                  isMandatory
                  control={form.control}
                  name="registrationValidity"
                  label="Registration Number Validity"
                  placeholder="DD/MM/YYYY"
                  dayPickerProps={{ disabled: { before: new Date() } }}
                />
              </div>

              {educationRows.map((_, index) => (
                <div key={index} className="mb-6 relative">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <div>
                      <div className="mb-2">
                        <InputField
                          isMandatory
                          control={form.control}
                          name={`educationRows.${index}.years`}
                          disabled={!isEditing}
                          placeholder="Enter years"
                          label="Education (Years)"
                          type="number"
                          maxLength={4}
                        />
                      </div>
                      <RadioButton
                        label="Display year of experience from here"
                        value="displayTop"
                        checked={selectedDisplayTop === index}
                        {...form.register(`educationRows.${index}.displayTop`)}
                        className="mt-1"
                        onChange={() => handleDisplayTopChange(index)}
                      />
                    </div>

                    <div>
                      <div className="mb-2">
                        <InputField
                          isMandatory
                          control={form.control}
                          name={`educationRows.${index}.body`}
                          disabled={!isEditing}
                          placeholder="Enter body"
                          label="Education (Body)"
                          type="text"
                          maxLength={100}
                        />
                      </div>
                    </div>

                    <div>
                      <div className="mb-2">
                        <SelectField
                          isMandatory
                          control={form.control}
                          disabled={!isEditing}
                          name={`educationRows.${index}.title`}
                          label="Education (Title)"
                          placeholder="Select title"
                          options={mapConcepts(educationTitle?.[0])}
                        />
                      </div>
                      <RadioButton
                        label="Display at the Top of HCP profile"
                        value="displayBottom"
                        checked={selectedDisplayBottom === index}
                        {...form.register(
                          `educationRows.${index}.displayBottom`
                        )}
                        className="mt-1"
                        onChange={() => handleDisplayBottomChange(index)}
                      />
                    </div>
                  </div>

                  {educationRows.length > 1 && (
                    <Button
                      variant="deleteBtn"
                      size="btn"
                      className="absolute right-[-50px] top-[40px] p-2 text-gray-700"
                      onClick={() => deleteRow(index)}
                      aria-label="Remove"
                      type="button"
                    >
                      <Trash size="1.2rem" />
                    </Button>
                  )}
                </div>
              ))}

              <Button
                variant="addBtn"
                size="btn"
                className="flex flex-row gap-2 items-center mt-5"
                onClick={addRow}
                type="button"
              >
                <CirclePlus size="1.2rem" /> Add more
              </Button>
            </FormSection>

            <FormSection>
              <h2 className="text-lg text-primary font-medium text-left mb-6">
                CONTACT DETAILS
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <DateField
                  isMandatory
                  control={form.control}
                  name="msaSigningDate"
                  label="MSA Signing Date"
                  placeholder="DD/MM/YYYY"
                  dayPickerProps={{
                    disabled: {
                      before: subMonths(new Date(), 1), // Disable dates older than 1 month
                      after: new Date(), // Disable future dates
                    },
                  }}
                />
                <FormItem>
                  <FormLabel>MSA contract upload</FormLabel>
                  <FileUpload
                    onChange={(file: File) => {
                      if (file) {
                        console.log('Selected PDF:', file);
                        setMsaContractFileName(file.name);
                      }
                    }}
                    defaultFileName={msaContractFileName}
                    accept="application/pdf"
                    maxFileSize={IMAGE_UPLOAD_SIZE}
                  />
                  <p className="text-sm mt-2 font-normal text-neutral-500">
                    PDF up to 5MB
                  </p>
                </FormItem>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <DateField
                  control={form.control}
                  disabled={!isEditing}
                  name="practitionerStartDate"
                  label="Practitioner Start Date"
                  placeholder="DD/MM/YYYY"
                  isMandatory
                  dayPickerProps={{
                    disabled: {
                      before: new Date(),
                      after: addDays(new Date(), 7),
                    },
                  }}
                />
                <DateField
                  control={form.control}
                  disabled={!isEditing}
                  name="practitionerEndDate"
                  label="Practitioner End Date"
                  placeholder="DD/MM/YYYY"
                  dayPickerProps={{
                    disabled: {
                      before: addDays(new Date(), 7),
                    },
                  }}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <PriceInputField
                  isMandatory
                  control={form.control}
                  disabled={!isEditing}
                  name="pricing"
                  label="Pricing"
                  placeholder=""
                  maxLength={4}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <FormItem>
                  <FormLabel>Indemnity upload</FormLabel>
                  <FileUpload
                    onChange={(file: File) => {
                      if (file) {
                        console.log('Selected PDF:', file);
                        setIndemnityFileName(file.name);
                      }
                    }}
                    defaultFileName={indemnityFileName}
                    accept="application/pdf"
                    maxFileSize={IMAGE_UPLOAD_SIZE}
                  />
                  <p className="text-sm mt-2 font-normal text-neutral-500">
                    PDF up to 5MB
                  </p>
                </FormItem>
                <DateField
                  isMandatory
                  control={form.control}
                  disabled={!isEditing}
                  name="indemnityValidity"
                  label="Indemnity Validity Until"
                  placeholder="DD/MM/YYYY"
                  dayPickerProps={{
                    disabled: {
                      before: new Date(),
                    },
                  }}
                />
              </div>
            </FormSection>

            {!id && (
              <div className="flex justify-end fixed bottom-0 w-full bg-white p-4 gap-4 right-0 border-t-2">
                <Button
                  className="bg-transparent text-black"
                  onClick={handleCancelClick}
                >
                  Cancel
                </Button>

                <Button type="submit" className="px-10" disabled={!isValid}>
                  Create Doctor
                </Button>
              </div>
            )}
            {id && isEditing && (
              <div className="flex justify-end fixed bottom-0 w-full bg-white p-4 gap-4 right-0 border-t-2">
                <Button
                  type="button"
                  className="bg-transparent text-black"
                  onClick={(e) => {
                    e.preventDefault();
                    mixPanelServiceInstance.track(
                      mixPanelEvents.DOCTOR_EVENTS.EDIT_DOCTOR_CANCELLED,
                      {
                        doctor_manager_id: userInfo?.sub,
                      }
                    );
                    setIsDeleteDialogOpen(true);
                  }}
                >
                  Cancel
                </Button>

                <Button type="submit" className="px-10" disabled={!isValid}>
                  Save Changes
                </Button>
              </div>
            )}
          </div>
        </form>
      </Form>

      <ConfirmationPopup
        title="Exit without saving?"
        description="The information you have entered will not be saved. Please confirm if you want to abandon this process."
        primaryButtonConfig={{
          label: 'No',
          handleClick: () => {
            setIsDeleteDialogOpen(false);
          },
          variant: 'default',
        }}
        secondaryButtonConfig={{
          label: 'Yes',
          handleClose: (e: React.MouseEvent<HTMLButtonElement>) => {
            toggleEdit(e);
            setIsDeleteDialogOpen(false);
          },
        }}
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      />
    </>
  );
};

export default DoctorForm;
